import React from "react";
import styled, { css, useTheme } from "styled-components";
import { Direction } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import {
  getNewLookTokens,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface NewLookDrawerTitleProps {
  symbol?: string;
  side?: string;
  /** Shown after the side when above 1 ("Long 10x"). */
  leverage?: number;
  status: string;
  chips?: string[];
  compact?: boolean;
}

const NewLookDrawerTitle = ({
  symbol,
  side,
  leverage,
  status,
  chips,
  compact,
}: NewLookDrawerTitleProps) => {
  const tokens = getNewLookTokens(useTheme());
  if (!symbol) return null;
  const notes = [status, ...(chips ?? [])].join(SEPARATOR);
  const meta = (
    <Meta>
      {side && (
        <Side
          style={{
            color: side === Direction.SHORT ? tokens.down : tokens.up,
          }}
        >
          {translateOptionName(side)}
          {!!leverage && leverage > 1 && <Leverage>{leverage}x</Leverage>}
        </Side>
      )}
      {side ? `${SEPARATOR}${notes}` : notes}
    </Meta>
  );
  return (
    <Title $compact={compact}>
      <IconWithFallback
        coinOrExchangeName={symbol}
        iconType={ICON_TYPES.COIN}
        pxIconSize={compact ? 32 : 24}
        iconStyle={compact ? COMPACT_ICON_STYLE : ICON_STYLE}
      />
      {compact ? (
        <Names>
          <SymbolName $compact={true} title={symbol}>
            {symbol}
          </SymbolName>
          {meta}
        </Names>
      ) : (
        <>
          <SymbolName>{symbol}</SymbolName>
          {meta}
        </>
      )}
    </Title>
  );
};

export default NewLookDrawerTitle;

export { parseLeverage } from "features/trades/NewLook/parseLeverage.ts";

const SEPARATOR = " · ";
const ICON_STYLE = { borderRadius: 12, flex: "none" };
const COMPACT_ICON_STYLE = { borderRadius: 16, flex: "none" };

const Title = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  min-height: 28px;
  ${(p) =>
    p.$compact &&
    css`
      flex-wrap: nowrap;
      min-width: 0;
    `}
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;
const SymbolName = styled.span<{ $compact?: boolean }>`
  font-size: 18px;
  line-height: 26px;
  font-weight: 520;
  letter-spacing: -0.01em;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  ${(p) =>
    p.$compact &&
    css`
      ${newLookPhoneType.headerTitle};
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}
`;
const Meta = styled.span`
  min-width: 0;
  ${newLookType.label};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Side = styled.span`
  font-weight: 500;
`;
const Leverage = styled.span`
  margin-left: 4px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
